import { lazy, Suspense, useMemo } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { listDocumentMeta } from '../lib/docMeta';
import { orphanDocuments, unresolvedLinks } from '../lib/linkIndex';
import { tagTree } from '../lib/inlineTags';
import { useStore, type VaultPanel as PanelId } from '../store/useStore';
import { useDocControllerContext } from '../hooks/DocControllerContext';
import { useT } from '../i18n/useT';
import { Icon } from './Icon';
import { JournalCalendar } from './JournalCalendar';

// The graph pulls in a canvas renderer and a layout pass that nothing else
// needs; it loads when the tab is opened rather than with the editor.
const GraphView = lazy(() => import('./GraphView').then((m) => ({ default: m.GraphView })));

const PANELS: { id: PanelId; labelKey: string; icon: 'tag' | 'link' | 'share' | 'calendar' }[] = [
  { id: 'tags', labelKey: 'vault.tags', icon: 'tag' },
  { id: 'links', labelKey: 'vault.links', icon: 'link' },
  { id: 'graph', labelKey: 'vault.graph', icon: 'share' },
  { id: 'journal', labelKey: 'vault.journal', icon: 'calendar' },
];

/**
 * The knowledge-base surface: tags, link hygiene, the graph and the journal.
 *
 * Only rendered in vault mode. markbird's default is a focused editor, and
 * these panels answer questions about a *collection* — which is a different
 * job, and one most documents never need.
 */
export function VaultPanel() {
  const t = useT();
  const panel = useStore((s) => s.vaultPanel);
  const setPanel = useStore((s) => s.setVaultPanel);

  if (!panel) return null;

  return (
    <aside className="mb-vault" aria-label={t('vault.title')}>
      <div className="mb-vault-head">
        <div className="mb-vault-tabs" role="tablist" aria-label={t('vault.title')}>
          {PANELS.map((p) => (
            <button
              key={p.id}
              role="tab"
              aria-selected={panel === p.id}
              className={panel === p.id ? 'active' : ''}
              onClick={() => setPanel(p.id)}
              aria-label={t(p.labelKey)}
              title={t(p.labelKey)}
            >
              <Icon name={p.icon} size={14} />
              {/* Four labels do not fit the panel; the active tab names itself. */}
              {panel === p.id && <span aria-hidden="true">{t(p.labelKey)}</span>}
            </button>
          ))}
        </div>
        <button
          className="mb-outline-close"
          onClick={() => setPanel(null)}
          aria-label={t('common.close')}
        >
          <Icon name="x" size={14} />
        </button>
      </div>

      <div className="mb-vault-body">
        {panel === 'tags' && <TagPane />}
        {panel === 'links' && <LinkPane />}
        {panel === 'journal' && <JournalCalendar />}
        {panel === 'graph' && (
          <Suspense fallback={<p className="mb-outline-empty">{t('common.loading')}</p>}>
            <GraphView />
          </Suspense>
        )}
      </div>
    </aside>
  );
}

/** Every tag in the vault, nested tags expanded to their parents. */
function TagPane() {
  const t = useT();
  const docs = useLiveQuery(() => listDocumentMeta(), [], []);
  const tagFilter = useStore((s) => s.tagFilter);
  const setTagFilter = useStore((s) => s.setTagFilter);

  const rows = useMemo(() => {
    const tree = tagTree(docs.map((d) => d.tags));
    // Count documents per tag, a parent counting everything nested beneath it.
    return tree.map((tag) => {
      const prefix = tag.toLowerCase();
      const count = docs.filter((d) =>
        d.tags.some((x) => {
          const key = x.toLowerCase();
          return key === prefix || key.startsWith(`${prefix}/`);
        }),
      ).length;
      return { tag, count, depth: tag.split('/').length - 1 };
    });
  }, [docs]);

  if (rows.length === 0) return <p className="mb-outline-empty">{t('vault.noTags')}</p>;

  return (
    <ul className="mb-tag-tree">
      {rows.map(({ tag, count, depth }) => (
        <li key={tag} style={{ paddingLeft: `${depth * 12}px` }}>
          <button
            className={tagFilter === tag ? 'active' : ''}
            aria-pressed={tagFilter === tag}
            onClick={() => setTagFilter(tagFilter === tag ? null : tag)}
          >
            <span className="mb-tag-tree-name">
              #{depth > 0 ? (tag.split('/').at(-1) ?? tag) : tag}
            </span>
            <span className="mb-tag-tree-count">{count}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * Link hygiene: references that point nowhere, and notes nothing points at.
 *
 * Both are useful precisely because they are *not* errors — an unresolved link
 * is usually a note you meant to write, which is why it can be created from
 * here in one click.
 */
function LinkPane() {
  const t = useT();
  const controller = useDocControllerContext();
  const unresolved = useLiveQuery(() => unresolvedLinks(), [], []);
  const orphans = useLiveQuery(() => orphanDocuments(), [], []);

  return (
    <div className="mb-linkpane">
      <h3 className="mb-vault-section">{t('vault.unresolved')}</h3>
      {unresolved.length === 0 ? (
        <p className="mb-outline-empty">{t('vault.noUnresolved')}</p>
      ) : (
        <ul className="mb-vault-list">
          {unresolved.map((link) => (
            <li key={link.toKey}>
              <button
                className="mb-vault-item"
                title={t('vault.createNote', { title: link.rawTarget })}
                onClick={() => void controller.createFrom(`# ${link.rawTarget}\n\n`)}
              >
                <Icon name="plus" size={13} />
                <span className="mb-vault-item-name">{link.rawTarget}</span>
                <span className="mb-vault-item-count">{link.from.length}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <h3 className="mb-vault-section">{t('vault.orphans')}</h3>
      {orphans.length === 0 ? (
        <p className="mb-outline-empty">{t('vault.noOrphans')}</p>
      ) : (
        <ul className="mb-vault-list">
          {orphans.map((doc) => (
            <li key={doc.id}>
              <button className="mb-vault-item" onClick={() => void controller.open(doc.id)}>
                <Icon name="dot" size={13} />
                <span className="mb-vault-item-name">{doc.title || t('common.untitled')}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
